import { useState } from "react";
import { toast } from "react-toastify";
import { Printer } from "lucide-react";
import api from "../services/api";
import {
  TransportFields,
  canGenerateGst,
  gstStatusBadgeClass,
  gstStatusLabel,
  hasEInvoicePdf,
  hasEWayBillPdf,
  toTransportForm,
  toTransportPayload,
} from "./salesInvoiceGst";

const formatDateTime = (value) => (value ? new Date(value).toLocaleString() : "-");

const DocumentCard = ({ title, doc, rows, canPrint, onPrint }) => (
  <div style={{ border: "1px solid var(--color-border, #e4e7ec)", borderRadius: "8px", padding: "12px 14px", flex: 1, minWidth: "240px" }}>
    <div className="flex items-center justify-between" style={{ marginBottom: "8px" }}>
      <strong>{title}</strong>
      <span className={`badge ${gstStatusBadgeClass[doc?.status] || "badge-gray"}`}>{gstStatusLabel[doc?.status] || doc?.status || "N/A"}</span>
    </div>
    {rows.map(([label, value]) => (
      <div key={label} style={{ fontSize: "13px", display: "flex", gap: "6px" }}>
        <span style={{ color: "var(--color-muted, #667085)", minWidth: "80px" }}>{label}</span>
        <span style={{ wordBreak: "break-all" }}>{value || "-"}</span>
      </div>
    ))}
    {doc?.errorMessage ? (
      <div style={{ fontSize: "12px", color: doc.status === "FAILED" ? "var(--color-danger, #b42318)" : "var(--color-muted, #667085)", marginTop: "6px" }}>
        {doc.errorMessage}
      </div>
    ) : null}
    <button
      type="button"
      className="secondary"
      style={{ marginTop: "10px", display: "inline-flex", alignItems: "center", gap: "5px" }}
      disabled={!canPrint}
      onClick={onPrint}
      title={canPrint ? `Open ${title} PDF` : "PDF is available once generated"}
    >
      <Printer size={13} /> Print PDF
    </button>
  </div>
);

/** E-invoice / e-way bill status of one sales invoice, with generation (when still pending) and PDF printing. */
const GstDocumentsModal = ({ invoice, onClose, onUpdated }) => {
  const [gst, setGst] = useState(invoice.gst);
  const [transport, setTransport] = useState(toTransportForm(invoice.gst?.transport));
  const [generating, setGenerating] = useState(false);

  const current = { ...invoice, gst };
  const generatable = canGenerateGst(current);
  const eWayBillPending = gst?.eWayBill?.status === "PENDING" || gst?.eWayBill?.status === "FAILED";

  const handleTransportChange = (e) => {
    const { name, value } = e.target;
    setTransport((prev) => ({ ...prev, [name]: value }));
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    setGenerating(true);
    try {
      const body = eWayBillPending ? toTransportPayload(transport) : null;
      const result = await api.post(`/api/v1/sales-invoices/${invoice.id}/gst/generate`, body ?? {});
      setGst(result);
      const failed = [result?.eInvoice, result?.eWayBill].some((doc) => doc?.status === "FAILED" || doc?.status === "PENDING");
      if (failed) toast.warn("Generation finished with pending / failed documents; see the details");
      else toast.success("E-invoice / e-way bill generated");
      onUpdated?.();
    } catch (error) {
      toast.error(error.message || "Failed to generate e-invoice / e-way bill");
    } finally {
      setGenerating(false);
    }
  };

  const print = async (kind) => {
    try {
      await api.openFile(`/api/v1/sales-invoices/${invoice.id}/gst/${kind}/pdf`);
    } catch (error) {
      toast.error(error.message || "Failed to open PDF");
    }
  };

  return (
    <div className="modal-overlay" role="presentation" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ maxWidth: "820px" }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold">E-Invoice / E-Way Bill — {invoice.invoiceNumber}</h3>
          <button className="close-btn" onClick={onClose}>
            Close
          </button>
        </div>

        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
          <DocumentCard
            title="E-Invoice"
            doc={gst?.eInvoice}
            rows={[
              ["IRN", gst?.eInvoice?.irn],
              ["Ack No.", gst?.eInvoice?.ackNo],
              ["Ack Date", gst?.eInvoice?.ackDate ? formatDateTime(gst.eInvoice.ackDate) : null],
            ]}
            canPrint={hasEInvoicePdf(current)}
            onPrint={() => print("e-invoice")}
          />
          <DocumentCard
            title="E-Way Bill"
            doc={gst?.eWayBill}
            rows={[
              ["EWB No.", gst?.eWayBill?.ewayBillNumber],
              ["Date", gst?.eWayBill?.ewayBillDate ? formatDateTime(gst.eWayBill.ewayBillDate) : null],
              ["Valid Until", gst?.eWayBill?.validUntil ? formatDateTime(gst.eWayBill.validUntil) : null],
            ]}
            canPrint={hasEWayBillPdf(current)}
            onPrint={() => print("e-way-bill")}
          />
        </div>

        <form onSubmit={handleGenerate}>
          {generatable && eWayBillPending ? (
            <>
              <h4 style={{ marginTop: "20px", marginBottom: "4px", fontWeight: 600 }}>Transport Details (E-Way Bill)</h4>
              <p style={{ fontSize: "12px", color: "var(--color-muted, #667085)", marginBottom: "10px" }}>
                By road give the vehicle number; by rail, air or ship give the transport document number and date. A transporter ID alone
                generates a Part-A e-way bill.
              </p>
              <TransportFields transport={transport} onChange={handleTransportChange} />
            </>
          ) : null}

          <div className="modal-actions">
            <button type="button" className="secondary" onClick={onClose}>
              Close
            </button>
            <button
              type="submit"
              className="primary"
              disabled={!generatable || generating}
              title={generatable ? "" : "Already generated or not applicable for this invoice"}
            >
              {generating ? "Generating..." : "Generate E-Invoice / E-Way Bill"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GstDocumentsModal;
